import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import DiagonalCarousel from '../components/ui/DiagonalCarousel.tsx';
import DemoVideoPopup from '../components/ui/DemoVideoPopup.tsx';
import SEO from '../components/SEO.tsx';
import { Settings, Users, Server, Brain, Mail, Rocket, Maximize2} from 'lucide-react';

interface HomeProps {
  onNavigate: (page: string) => void;
}

const Home: React.FC<HomeProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [currentText, setCurrentText] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const switchingTexts = t('home.hero.title.switchingTexts', { returnObjects: true }) as string[];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % switchingTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const carouselItems = [
    {
      id: 1,
      image: '/screens/catalog.png',
      keyword: t('home.carousel.items.premiumRules.keyword'),
      description: t('home.carousel.items.premiumRules.description')
    },
    {
      id: 3,
      image: '/screens/scans.png',
      keyword: t('home.carousel.items.monitor.keyword'),
      description: t('home.carousel.items.monitor.description')
    },
    {
      id: 6,
      image: '/screens/workflow.png',
      keyword: t('home.carousel.items.visualize.keyword'),
      description: t('home.carousel.items.visualize.description')
    },
    {
      id: 7,
      image: '/screens/builder.png',
      keyword: t('home.carousel.items.builder.keyword'),
      description: t('home.carousel.items.builder.description')
    }
  ];

  const features = [
    {
      title: t('home.whyPremium.features.configuration.title'),
      description: t('home.whyPremium.features.configuration.description'),
      items: t('home.whyPremium.features.configuration.items', { returnObjects: true }) as string[]
    },
    {
      title: t('home.whyPremium.features.visualization.title'),
      description: t('home.whyPremium.features.visualization.description'),
      items: t('home.whyPremium.features.visualization.items', { returnObjects: true }) as string[]
    },
    {
      title: t('home.whyPremium.features.artificialIntelligence.title'),
      description: t('home.whyPremium.features.artificialIntelligence.description'),
      items: t('home.whyPremium.features.artificialIntelligence.items', { returnObjects: true }) as string[]
    },
    {
      title: t('home.whyPremium.features.historyVisualization.title'),
      description: t('home.whyPremium.features.historyVisualization.description'),
      items: t('home.whyPremium.features.historyVisualization.items', { returnObjects: true }) as string[]
    }
  ];

  const supportFeatures = [
    {
      title: t('home.prioritySupport.features.customRules.title'),
      description: t('home.prioritySupport.features.customRules.description'),
      icon: Settings
    },
    {
      title: t('home.prioritySupport.features.cloudExperts.title'),
      description: t('home.prioritySupport.features.cloudExperts.description'),
      icon: Users
    },
    {
      title: t('home.prioritySupport.features.customAddons.title'),
      description: t('home.prioritySupport.features.customAddons.description'),
      icon: Rocket
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Kexa Premium - Advanced Cloud Security, Compliance & FinOps"
        description="Transform your cloud security with Kexa Premium. AI-powered compliance monitoring, custom rules, and visualization. Get enterprise-grade protection."
        keywords="kexa premium, cloud security platform, compliance monitoring, AWS security scanner, Azure security, GCP monitoring, DevOps security, infrastructure security"
      />
      <section className="bg-gray-50 py-16 md:py-24 pt-24 sm:pt-28 md:pt-24 relative min-h-screen 2xl:min-h-[80vh] flex items-center">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2353adbb%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
        <div className="container-custom relative w-full">
          <DemoVideoPopup />
          {/* Mobile Layout: Title → Carousel → Catchphrase & CTA */}
          <div className="lg:hidden">
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl mb-6 font-bold leading-tight animate-slide-up">
                {t('home.hero.title.complete')}
                <br />
                <div className="h-[1.2em] flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentText}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5 }}
                      className="inline-block"
                    >
                      <GradientText variant={currentText % 2 === 0 ? "primary" : "accent"}>
                        {switchingTexts[currentText]}
                      </GradientText>
                    </motion.span>
                  </AnimatePresence>
                </div>
                {t('home.hero.title.solution')}
              </h1>
            </div>
            <div className="mb-8">
              <DiagonalCarousel 
                items={carouselItems}
                autoPlay={true}
                interval={5000}
              />
            </div>
            <div className="text-center">
              <p className="text-lg sm:text-lg text-gray-600 mb-8 animate-fade-in">
                {t('home.hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 animate-slide-up">
                <Button
                  onClick={() => onNavigate('plans')}
                  variant="gradient"
                  size="lg"
                  leftIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>}
                >
                  {t('home.hero.buttons.explorePlans')}
                </Button>
                <Button
                  onClick={() => onNavigate('deploy')}
                  variant="outline"
                  size="lg"
                  rightIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-primary"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
                    </svg>
                  }
                >
                  {t('home.hero.buttons.deployNow')}
                </Button>
              </div>
              <div className="flex items-center justify-center gap-8 text-sm text-gray-500 mt-6">
                <div className="flex items-center gap-2">
                  <Server size={16} className="text-primary" />
                  <span>{t('common.features.fullOnPremise')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Brain size={16} className="text-primary" />
                  <span>{t('common.features.aiPowered')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout: Two-column */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-5 text-left">
              <h1 className="text-5xl xl:text-6xl mb-6 font-bold leading-tight animate-slide-up">
                {t('home.hero.title.complete')}
                <br />
                <div className="h-[1.2em] flex items-center justify-start">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentText}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5 }}
                      className="inline-block"
                    >
                      <GradientText variant={currentText % 2 === 0 ? "primary" : "accent"}>
                        {switchingTexts[currentText]}
                      </GradientText>
                    </motion.span>
                  </AnimatePresence>
                </div>
                {t('home.hero.title.solution')}
              </h1>
              <p className="text-xl text-gray-600 mb-8 animate-fade-in">
                {t('home.hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-start items-center mb-8 animate-slide-up">
                <Button
                  onClick={() => onNavigate('plans')}
                  variant="gradient"
                  size="lg"
                  leftIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>}
                >
                  {t('home.hero.buttons.explorePlans')}
                </Button>
                <Button
                  onClick={() => onNavigate('deploy')}
                  variant="outline"
                  size="lg"
                  rightIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-primary"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
                    </svg>
                  }
                >
                  {t('home.hero.buttons.deployNow')}
                </Button>
              </div>
              <div className="flex items-center gap-8 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Server size={16} className="text-primary" />
                  <span>{t('common.features.fullOnPremise')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Brain size={16} className="text-primary" />
                  <span>{t('common.features.aiPowered')}</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <DiagonalCarousel 
                items={carouselItems}
                autoPlay={true}
                interval={5000}
              />
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-3xl sm:text-xl md:text-2xl lg:text-5xl mb-4 font-bold">
              {t('home.whyPremium.title.prefix')}
              <GradientText variant="primary">{t('home.whyPremium.title.highlight')}</GradientText>
              {t('home.whyPremium.title.suffix')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.whyPremium.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="bg-gradient-card rounded-2xl p-8 shadow-card border border-white/50 hover:shadow-modern transition-all duration-300 hover:scale-105 animate-fade-in group">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-primary rounded-xl p-3 flex-shrink-0 shadow-button group-hover:shadow-button-hover transition-all duration-300">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl mb-2 font-semibold">
                      <GradientText variant="primary">{feature.title}</GradientText>
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {feature.description}
                    </p>
                    <ul className="space-y-2">
                      {feature.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-center text-gray-700">
                          <div className="w-4 h-4 bg-gradient-primary rounded-full mr-2 flex-shrink-0 flex items-center justify-center">
                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl h-32 group-hover:h-64 flex items-center justify-center border border-gray-200/50 group-hover:border-primary/30 transition-all duration-500 overflow-hidden relative">
                  {index === 3 ? (
                    <div className="flex items-center justify-center text-center p-4">
                      <span className="text-sm font-medium text-gray-600">Release end 2025</span>
                    </div>
                  ) : (
                    <>
                      <img 
                        src={`/screens/${['catalog.png', 'scans-2.png', 'ai-remediate.png', 'workflow.png'][index]}`}
                        alt={`${feature.title} screenshot`}
                        className="w-full h-full object-cover rounded-xl transition-all duration-500"
                      />
                      <button
                        onClick={() => setFullscreenImage(`/screens/${['catalog.png', 'scans-2.png', 'ai-remediate.png', 'workflow.png'][index]}`)}
                        className="absolute top-2 right-2 p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm z-10"
                        aria-label="View fullscreen"
                      >
                        <Maximize2 size={16} className="text-white" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-3xl sm:text-xl md:text-2xl lg:text-5xl mb-4 font-bold">
              {t('home.aiPowered.title.prefix')}
              <GradientText variant="primary">{t('home.aiPowered.title.highlight')}</GradientText>
              {t('home.aiPowered.title.suffix')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.aiPowered.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in">
            {['/screens/ai-remediate.png', '/screens/ai-history.png', '/screens/ai-title-remediate.png'].map((screenshot, index) => (
              <div key={index} className="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200/50 hover:border-primary/30 transition-all duration-500 overflow-hidden aspect-video">
                <img 
                  src={screenshot}
                  alt={`AI Feature ${index + 1}`}
                  className="w-full h-full object-cover rounded-xl transition-all duration-500 group-hover:scale-105"
                />
                <button
                  onClick={() => setFullscreenImage(screenshot)}
                  className="absolute top-2 right-2 p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm z-10"
                  aria-label="View fullscreen"
                >
                  <Maximize2 size={16} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-gray-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-3xl sm:text-xl md:text-2xl lg:text-5xl mb-4 font-bold">
              {t('home.standardCompliance.title.prefix')}
              <GradientText variant="primary">{t('home.standardCompliance.title.highlight')}</GradientText>
              {t('home.standardCompliance.title.suffix')}
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              {t('home.standardCompliance.subtitle')}
            </p>
          </div>
          
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 animate-fade-in">
            {/* Screenshot - Middle on mobile, right side on desktop */}
            <div className="lg:w-1/2 lg:order-2">
              <div className="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200/50 hover:border-primary/30 transition-all duration-500 overflow-hidden">
                <img 
                  src="/screens/ai-documents.png"
                  alt="Standard Compliance Documentation"
                  className="w-full h-full object-cover rounded-xl transition-all duration-500 group-hover:scale-105"
                />
                <button
                  onClick={() => setFullscreenImage('/screens/ai-documents.png')}
                  className="absolute top-2 right-2 p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm z-10"
                  aria-label="View fullscreen"
                >
                  <Maximize2 size={16} className="text-white" />
                </button>
              </div>
            </div>
            
            {/* Features - Bottom on mobile, left side on desktop */}
            <div className="lg:w-1/2 lg:order-1 text-center lg:text-left">
              <div className="space-y-6 md:space-y-8">
                <div className="flex flex-col sm:flex-row items-start sm:space-x-4 text-center sm:text-left">
                  <div className="bg-gradient-primary rounded-xl p-3 flex-shrink-0 shadow-button mb-4 sm:mb-0 mx-auto sm:mx-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold mb-2">
                      <GradientText variant="primary">{t('home.standardCompliance.features.cisBenchmarks.title')}</GradientText>
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      {t('home.standardCompliance.features.cisBenchmarks.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row items-start sm:space-x-4 text-center sm:text-left">
                  <div className="bg-gradient-primary rounded-xl p-3 flex-shrink-0 shadow-button mb-4 sm:mb-0 mx-auto sm:mx-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold mb-2">
                      <GradientText variant="primary">{t('home.standardCompliance.features.expertRules.title')}</GradientText>
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      {t('home.standardCompliance.features.expertRules.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row items-start sm:space-x-4 text-center sm:text-left">
                  <div className="bg-gradient-primary rounded-xl p-3 flex-shrink-0 shadow-button mb-4 sm:mb-0 mx-auto sm:mx-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold mb-2">
                      <GradientText variant="primary">{t('home.standardCompliance.features.customDocuments.title')}</GradientText>
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      {t('home.standardCompliance.features.customDocuments.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('home.prioritySupport.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.prioritySupport.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {supportFeatures.map((feature, index) => (
              <div key={index} className="p-8">
                <div className="text-center">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-primary to-accent rounded-xl">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">
                    <GradientText variant="primary">{feature.title}</GradientText>
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 animate-slide-up">
            {t('common.buttons.readyToGetStarted')}
          </h2>
          <div className="w-16 h-px bg-white/40 mx-auto mb-8 animate-fade-in"></div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
            <Button
              onClick={() => onNavigate('plans')}
              variant="cta"
              size="lg"
              leftIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>}
            >
              Explore Plans
            </Button>
            <Button
              onClick={() => onNavigate('contact')}
              variant="cta-glass"
              size="lg"
              rightIcon={<Mail size={20} />}
            >
              {t('common.buttons.contactUs')}
            </Button>
            <Button
              onClick={() => onNavigate('deploy')}
              variant="cta-glass"
              size="lg"
              rightIcon={<Rocket size={20} />}
            >
              {t('common.buttons.startDeploying')}
            </Button>
          </div>
        </div>
      </section>

      {fullscreenImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img 
              src={fullscreenImage} 
              alt="Fullscreen view"
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute top-4 right-4 p-2 bg-black/40 hover:bg-black/60 rounded-lg backdrop-blur-sm"
              aria-label="Close fullscreen"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;